import { ChangeEvent, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  darken,
  lighten,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { ArrayElement } from '@/__configs/config_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { BulkActions } from '@/redux/slices/slice_types';
import { setBulkErrors } from '@/redux/slices/dataTableSlice';
import {
  selectBulkActionConstants,
  selectCheckedTableRows,
  selectCurrentTableBulkActions,
  selectCurrentTableBulkActionsByStatusStage,
  selectCurrentTableKey,
  selectUserColumns
} from '@/redux/selectors/dataTableSelectors';
import { useToast } from '@/components/_common/Toast';
import axios from '@/_utils/axios';
import { displayBulkActionIdent, titleCase } from '@/_utils/utils';
import { Table } from '@/components/_common/Table';
import PolarisIcon from '@/components/_common/PolarisIcon';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import { useConfigValue } from '@/components/_hooks/useConfigValue';

type BulkActionDialogProps = {
  isOpen: boolean;
  action: string;
  onConfirm: (maintainSelections: boolean) => void;
  onCancel: () => void;
};

export const formatFormValues = (values: any) => {
  const valueKeys = Object.keys(values);
  if (!values || valueKeys.length <= 0) {
    return null;
  }

  const obj: any = {};

  valueKeys.forEach((key) => {
    if (typeof values[key] === 'object' && values[key]?.id) {
      obj[key] = values[key].id;
    } else if (values[key] !== null) {
      obj[key] = values[key];
    }
  });

  return obj;
};

const BulkActionDialog = ({ isOpen, action, onConfirm, onCancel }: BulkActionDialogProps) => {
  const dispatch = useAppDispatch();
  const { success, error } = useToast();
  const baseColumns = useAppSelector(selectUserColumns);
  const columns = useMemo(() => baseColumns.filter((col) => !col.isOverflow && !col.id.includes('action')), [baseColumns]);
  const data = useAppSelector(selectCheckedTableRows);
  const selectActions = useAppSelector(selectCurrentTableBulkActions);
  const selectActionsWithStage = useAppSelector(selectCurrentTableBulkActionsByStatusStage);
  const tableKey = useAppSelector(selectCurrentTableKey);
  const bulkActionConstants = useAppSelector(selectBulkActionConstants);
  const bulkActionMaintainSelections = useConfigValue<string[]>(['bulkActionMaintainSelections']) || [];
  const [isLoading, setIsLoading] = useState(false);
  const [scrollable, setScrollable] = useState<boolean | null>(null);
  const [scrollEnd, setScrollEnd] = useState<'left' | 'right' | null>('left');
  const [isApplicableExpanded, setIsApplicableExpanded] = useState(true);
  const [isUnapplicableExpanded, setIsUnapplicableExpanded] = useState(false);
  const [status_reason, setReason] = useState('');
  const [maintainSelections, setMaintainSelections] = useState(bulkActionMaintainSelections.includes(action) || false);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<any>(null);

  const currentAction = useMemo(() => selectActions?.[action], [selectActions, action]);
  const isCancelAction = currentAction?.ident === 'cancel';

  const applicableData = data.filter((job: any) => {
    if (!selectActionsWithStage) return false;
    return selectActionsWithStage[job.status]?.actions.includes(action) || selectActionsWithStage[job.status]?.stage[job.stage]?.length;
  });

  const notApplicableData = data.filter((job: any) => {
    if (!selectActionsWithStage) return false;
    return !selectActionsWithStage[job.status]?.actions.includes(action) && !selectActionsWithStage[job.status]?.stage[job.stage]?.length;
  });

  const handleSubmit = async () => {
    if (!currentAction) return;
    setIsLoading(true);

    const label = displayBulkActionIdent(currentAction.ident);
    try {
      if (formRef.current && !formRef.current.validateForm()) {
        return;
      }

      const formattedValues = formatFormValues(formRef.current?.state?.formData ?? {});
      let requestBody: any = { resource_identifiers: applicableData.map((selected: ArrayElement<typeof data>) => selected?.id) };
      if (isCancelAction) {
        requestBody['action_payload'] = { status_reason };
      }
      if (formattedValues && Object.keys(formattedValues).length > 0) {
        requestBody = {
          ...requestBody,
          action_payload: {
            ...(requestBody.action_payload ?? {}),
            ...formattedValues
          }
        };
      }
      const response = await axios({
        method: 'post',
        url: `bulk_actions/${currentAction.action_type}/${currentAction.ident}/`,
        data: requestBody,
        baseURL: `/api/v${currentAction?.version || 1}/`
      });

      success(
        response.data?.message?.length
          ? response.data?.message
          : `${response.data?.data?.queue_ids.length || response.data?.data?.queued_ids.length} job(s) submitted to ${label}`
      );
      if (response.data?.data?.ineligible_ids?.length) {
        error(`Failed to ${label}: ${response.data?.data?.ineligible_ids.join(',')}`);
      } else if (response.data?.data?.ineligible_ids && !Array.isArray(response.data?.data?.ineligible_ids)) {
        const responseIds = response.data?.data?.ineligible_ids;
        const ineligibleIds = Object.keys(responseIds).map((key) => [key, responseIds[key]]);
        dispatch(setBulkErrors(ineligibleIds));
      }

      onConfirm(maintainSelections);
    } catch (err) {
      console.error(`Failed to bulk ${label}`, err);
      error(`Failed bulk ${label}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      onCancel();
    }
  };

  const getScrollEnd = (ref: RefObject<any>) => {
    if (Math.floor(ref.current.scrollLeft) === 0) return 'left';
    else if (Math.ceil(ref.current.scrollLeft) + ref.current.clientWidth >= ref.current.scrollWidth) return 'right';
    else return null;
  };

  const handleScroll = () => {
    const newScrollEnd = getScrollEnd(scrollableRef);
    if (newScrollEnd !== scrollEnd) {
      setScrollEnd(newScrollEnd);
    }
  };

  const handleReasonInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  };

  useEffect(() => {
    if (scrollableRef.current) {
      const isScrollable = scrollableRef.current?.scrollWidth > scrollableRef.current?.clientWidth;
      setScrollable(isScrollable);
      window.addEventListener('resize', () => {
        const newScrollable = scrollableRef.current && scrollableRef.current.scrollWidth > scrollableRef.current.clientWidth;
        if (scrollableRef.current && newScrollable !== scrollable) {
          setScrollable(newScrollable);
        }
      });
    }
    return () => {
      window.removeEventListener('resize', () => {});
    };
  }, [scrollableRef.current, scrollable]);

  const submitButtonLabel =
    currentAction?.ident && currentAction?.action_type
      ? `${displayBulkActionIdent(currentAction.ident)} ${titleCase(currentAction.action_type)}`
      : 'Submit';

  return (
    <StyledDialog open={isOpen} onClose={handleCancel} maxWidth="xl" fullWidth>
      <DialogTitle display="flex" flexDirection="column" gap={1}>
        Confirm Bulk {currentAction?.ident ? displayBulkActionIdent(currentAction.ident) : 'Action'}
        <Typography color="#B7B7B7">
          {data.length} total {displayBulkActionIdent(tableKey || 'items')}
        </Typography>
      </DialogTitle>
      <CloseButton onClick={handleCancel} disabled={isLoading}>
        <PolarisIcon icon="close" />
      </CloseButton>
      <Content>
        <ScrollableDiv
          scrollable={scrollable || false}
          ref={scrollableRef}
          onScroll={handleScroll}
          last={columns[columns.length - 1]?.id}
          first={columns[0]?.id}
        >
          <div style={{ display: 'grid' }}>
            {notApplicableData.length > 0 && (
              <>
                <StyledAccordionWrapper>
                  <StyledAccordion expanded={isUnapplicableExpanded} onChange={() => setIsUnapplicableExpanded(!isUnapplicableExpanded)}>
                    <StyledAccordionSummary expandIcon={<ExpandableIcon icon="down" expanded={isUnapplicableExpanded} height={15} />}>
                      Not Applicable <StyledBadge>{notApplicableData.length}</StyledBadge>
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>
                      <Table columns={columns} data={notApplicableData} scrollable={false} scrollEnd={null} />
                    </StyledAccordionDetails>
                  </StyledAccordion>
                </StyledAccordionWrapper>
                <StyledDivider />
              </>
            )}

            <StyledAccordionWrapper>
              <StyledAccordion expanded={isApplicableExpanded} onChange={() => setIsApplicableExpanded(!isApplicableExpanded)}>
                <StyledAccordionSummary expandIcon={<ExpandableIcon icon="down" expanded={isApplicableExpanded} height={15} />}>
                  Applicable <StyledBadge>{applicableData.length}</StyledBadge>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Table columns={columns} data={applicableData} scrollable={false} scrollEnd={null} />
                </StyledAccordionDetails>
              </StyledAccordion>
            </StyledAccordionWrapper>
            {isCancelAction && (
              <TextField
                fullWidth
                id="reason"
                label={`Cancel Reason${status_reason.length > 200 ? `: ${status_reason.length} / 255 characters` : ''}`}
                multiline
                minRows={4}
                maxRows={8}
                value={status_reason}
                onChange={handleReasonInputChange}
                sx={{ mt: '2rem', maxWidth: '800px' }}
                inputProps={{ maxLength: 255 }}
              />
            )}
          </div>
          {tableKey && bulkActionConstants?.[tableKey]?.[action as keyof BulkActions]?.action_schema ? (
            <SchemaFormWrapper>
              <SchemaForm
                isInsideModal
                formRef={formRef}
                schema={bulkActionConstants?.[tableKey]?.[action as keyof BulkActions]?.action_schema || {}}
              />
            </SchemaFormWrapper>
          ) : null}
        </ScrollableDiv>
      </Content>
      <Actions>
        <Button variant="outlined" sx={{ mr: 'auto' }} disabled={isLoading} onClick={handleCancel}>
          Add More
        </Button>
        <FormControlLabel
          control={<Checkbox checked={maintainSelections} onChange={() => setMaintainSelections((prev) => !prev)} />}
          label="Maintain selections after action"
        />
        <Button variant="text" color="default" disabled={isLoading} onClick={handleCancel}>
          Cancel
        </Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={isLoading} onClick={handleSubmit}>
          {submitButtonLabel}
        </LoadingButton>
      </Actions>
    </StyledDialog>
  );
};

export default BulkActionDialog;

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    maxWidth: '2205px'
  }
}));

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(2)};
  right: ${({ theme }) => theme.spacing(2)};
`;

const Content = styled(DialogContent)(() => ({
  maxHeight: '90dvh',
  padding: 0
}));

const ScrollableDiv = styled.div<{ scrollable: boolean; last: string; first: string }>(({ theme }) => ({
  margin: `0 ${theme.spacing(2)}`,
  paddingBottom: theme.spacing(2),
  overflowX: 'scroll'
}));

const Actions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2)
}));

const StyledAccordionWrapper = styled.div(({ theme }) => ({
  padding: theme.spacing(1),
  background: theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.06) : lighten(theme.palette.background.paper, 0.06)
}));

const StyledAccordion = styled(Accordion)(() => ({
  flex: 1,
  '&.MuiAccordion-root.Mui-expanded': {
    margin: 0
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  '&.MuiAccordionSummary-root': {
    flexDirection: 'row-reverse',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    '&.Mui-expanded': {
      minHeight: 'auto',
      '& .MuiAccordionSummary-content': {
        marginTop: 0,
        marginBottom: 0
      }
    }
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    '& svg': {
      transform: 'rotate(-90deg)'
    },
    '&.Mui-expanded': {
      transform: 'none',
      '& svg': {
        transform: 'rotate(0deg)'
      }
    }
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  maxHeight: '30dvh',
  overflow: 'auto',
  backgroundColor: theme.palette.background.paper,
  '&.MuiAccordionDetails-root': {
    padding: 0
  }
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: '#13141b',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1)
}));

const StyledBadge = styled.div(({ theme }) => ({
  width: 24,
  height: 24,
  lineHeight: '15px',
  borderRadius: '50%',
  padding: theme.spacing(0.5),
  marginLeft: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.getContrastText(theme.palette.background.paper),
  fontSize: theme.typography.caption.fontSize,
  textAlign: 'center'
}));

const ExpandableIcon = styled(PolarisIcon, { shouldForwardProp: (prop) => !['expanded'].includes(prop) })<{ expanded: boolean }>(
  ({ expanded, theme }) => ({
    opacity: expanded ? 1 : 0.6,
    color: expanded ? theme.palette.primary.main : theme.palette.text.primary,
    marginRight: theme.spacing(2),
    '&:hover': {
      opacity: 1
    },
    transition: 'all 0.2s ease-in-out'
  })
);

const SchemaFormWrapper = styled.div`
  margin-top: ${(props) => props.theme.spacing(3.5)};
`;
